import { z } from "zod";

import {
  DEFAULT_CARD_DISPLAY_OPTIONS,
  type CardDisplayOptions,
} from "#app/components/issue-card";
import { FILTER_OPERATORS, type FilterState } from "#app/lib/filters/types";
import type { SortDirection } from "#app/lib/data";

// ---------------------------------------------------------------------------
// ViewConfig — the Customize view blob a saved view carries
// ---------------------------------------------------------------------------
//
// `EntityView.config` is opaque JSON on the server by design: the panel grows
// a control without a schema change. That makes this file the only place that
// knows the blob's shape, and the only place that decides what a missing or
// malformed key falls back to.
//
// Everything here is a DISPLAY decision, never a data one. A saved view says
// how the projects are grouped, which of them are hidden, which columns a
// list shows, how the cards read, and — on a dashboard view — how its board
// packs and refreshes; it never says which projects exist. One blob serves
// every view type, so a key a body does not understand simply sits unread.

const filterValueSchema = z.object({
  field: z.string(),
  operator: z.enum(FILTER_OPERATORS),
  value: z.unknown(),
});

const filterBlockSchema = z.object({
  logic: z.enum(["AND", "OR"]),
  filters: z.array(filterValueSchema),
});

const filterStateSchema = z.object({
  blocks: z.array(filterBlockSchema),
  logic: z.enum(["AND", "OR"]),
});

const cardDisplayOptionsSchema = z.object({
  fieldLabelMode: z.enum(["text", "icon"]),
  textLabelLayout: z.enum(["horizontal", "vertical"]),
  fieldLayout: z.enum(["wrap", "list"]),
  showAddress: z.boolean(),
  showTeams: z.boolean(),
  showStage: z.boolean(),
  showWorkflow: z.boolean(),
  showProjectType: z.boolean(),
  showCreated: z.boolean(),
  showUpdated: z.boolean(),
  showAge: z.boolean(),
  shownFieldIds: z.array(z.string()),
  showEmptyFields: z.boolean(),
  itemOrder: z.array(z.string()),
});

// Member catches build FRESH arrays: `.catch(value)` hands the same instance
// to every parse, and these land in mutable draft state.
const viewColumnsSchema = z.object({
  order: z.array(z.string()).catch(() => []),
  hidden: z.array(z.string()).catch(() => []),
  shown: z.array(z.string()).catch(() => []),
});

export const EMPTY_FILTER_STATE: FilterState = { blocks: [], logic: "AND" };

export type ViewColumns = {
  /** Column ids in display order; empty means the table's default order.
   *  Ids the table no longer renders keep their slots (gated or removed
   *  columns), and ids it gained slot in at their default position. */
  order: string[];
  /** Default-shown column ids the view hides. Kept sorted so the same set
   *  always serializes — and dirty-compares — identically. */
  hidden: string[];
  /** Default-hidden column ids the view shows. Absence in either list keeps
   *  meaning "the table's default", which is what lets a workspace add a
   *  custom field without rewriting every saved view. Kept sorted. */
  shown: string[];
};

/** Deep-frozen: this one instance is the config default, the whole-object
 *  schema fallback and what Reset drafts, so an in-place sort/push against
 *  it would silently edit every view at once — freezing turns that into a
 *  loud throw instead. Writers always build fresh arrays. */
export const EMPTY_VIEW_COLUMNS: ViewColumns = Object.freeze({
  order: Object.freeze([] as string[]),
  hidden: Object.freeze([] as string[]),
  shown: Object.freeze([] as string[]),
}) as ViewColumns;

/** A board's column width rung — the panel's "Card size" row. */
export type BoardCardSize = "medium" | "large";

export type ViewConfig = {
  /** The "Only me" toggle's saved position — the view opens with the
   *  current-user filter already applied. */
  onlyMe: boolean;
  /** Include archived projects. Off, the view's own base predicate excludes
   *  them; on, it stops filtering and archived rows join the list. */
  showArchived: boolean;
  /** A team's list: whether Projects merely SHARED with the team join the
   *  ones assigned to it (`sharedWithTeamIds`). On by default — the tab is
   *  everything the team is connected to; off, it is the team's own work.
   *  Read only under a team scope. */
  showShared: boolean;
  /** Include projects sitting in a `done` stage. Off, the view's own base
   *  predicate narrows the stage set to every other stage (the issue filter
   *  has no exclusion, so the view names the stages it keeps). */
  showCompleted: boolean;
  /** Include projects sitting in a `canceled` stage — the same mechanism. */
  showCanceled: boolean;
  /** Start the empty stage buckets collapsed — a board's empty columns as
   *  rails, a list's empty sections closed. Only meaningful for a grouping
   *  whose buckets are a bounded, ordered set (stage); every bucket still
   *  renders. The default is the view body's (`viewConfigDefaults`): on for
   *  a list, where an open empty section is a header over nothing; off for a
   *  board, where an open empty column is the drop target for moving a card
   *  into that stage. */
  collapseEmptyStages: boolean;
  /** The grouping dimension key — `none` for a flat list. */
  groupBy: string;
  groupDirection: SortDirection;
  /** The group buckets (a list's sections, a board's columns) explicitly
   *  collapsed in this view. Keys are group values of the active `groupBy`
   *  dimension; a key from another dimension matches nothing and sits
   *  harmlessly. Kept sorted so the same set always serializes — and
   *  dirty-compares — identically. */
  collapsedGroups: string[];
  /** The empty stage buckets a person opened while `collapseEmptyStages`
   *  had them starting closed — the override in the other direction, so an
   *  expanded empty column survives a reload the way a collapsed full one
   *  does. Kept sorted like `collapsedGroups`. */
  expandedGroups: string[];
  /** The row-order field key — `none` for the natural order (a list's
   *  creation order, a board column's manual order). */
  sortBy: string;
  sortDirection: SortDirection;
  /** List column state — which columns show and their order. The panel's
   *  Fields page, the table's header "+" and in-table column drags all edit
   *  this. Board views ignore it (cards carry `card.shownFieldIds`). */
  columns: ViewColumns;
  /** Board-card display knobs. Ignored by list views. */
  card: CardDisplayOptions;
  /** Board column width — every card in the view is the column's width, so
   *  this is what "Card size" sizes. Ignored by list and dashboard views. */
  cardSize: BoardCardSize;
  /** The view's saved quick filters — the Simple tab of the Customize view
   *  panel's Filters page and of the toolbar filter panel, which edit one
   *  set through `IssueFilterProvider`. */
  filters: FilterState;
  /** The view's saved advanced filters — the Filters page's Advanced tab.
   *  Its own state because the two editors keep separate `FilterState`s
   *  (Simple edits one block by field, Advanced owns blocks and a top-level
   *  logic), and both apply at once: the views AND them. */
  advancedFilters: FilterState;
  /** Dashboard views: re-run every widget's queries on a fixed cadence while
   *  the tab is visible. List and board views ignore it. */
  autoRefresh: boolean;
  /** Dashboard views: pack the board and hold it packed through every later
   *  gesture. The packed POSITIONS are widget state and save with the board;
   *  this flag is what keeps the grid packing. List and board views ignore
   *  it. */
  autoLayout: boolean;
};

export const DEFAULT_VIEW_CONFIG: ViewConfig = {
  onlyMe: false,
  showArchived: false,
  showShared: true,
  showCompleted: true,
  showCanceled: true,
  collapseEmptyStages: false,
  groupBy: "stage",
  groupDirection: "asc",
  collapsedGroups: [],
  expandedGroups: [],
  sortBy: "none",
  sortDirection: "asc",
  columns: EMPTY_VIEW_COLUMNS,
  card: DEFAULT_CARD_DISPLAY_OPTIONS,
  cardSize: "large",
  filters: EMPTY_FILTER_STATE,
  advancedFilters: EMPTY_FILTER_STATE,
  autoRefresh: false,
  autoLayout: false,
};

const LIST_VIEW_CONFIG: ViewConfig = {
  ...DEFAULT_VIEW_CONFIG,
  collapseEmptyStages: true,
};

// A team's list spans workflows, so there is no one set of stages to group
// by — it opens flat, like every other cross-workflow list.
// The column order a team's Projects tab opens with: what needs attention
// and who is on it lead, where it sits follows — Follow up and Assignees
// before Teams and Workflow (on a team page the team is mostly a given).
// Ids are the issue table's column ids (`issue-columns.tsx`); a column not
// named slots in at its default position.
const TEAM_LIST_VIEW_CONFIG: ViewConfig = {
  ...LIST_VIEW_CONFIG,
  groupBy: "none",
  columns: {
    ...EMPTY_VIEW_COLUMNS,
    order: ["name", "age", "assignees", "teams", "workflow", "address", "creationDate", "lastUpdateDate"],
  },
};

/**
 * The defaults a view body renders from — `DEFAULT_VIEW_CONFIG` with the keys
 * whose default depends on the body. `viewType` is the row's `type` (`list`,
 * `board`, `dashboard`); the list is the one body that starts its empty stage
 * buckets collapsed. `scopeKind` is the view's scope: a team's list opens
 * ungrouped (stage grouping needs a single workflow).
 */
export function viewConfigDefaults(viewType: string, scopeKind?: string): ViewConfig {
  if (viewType === "list") {
    return scopeKind === "team" ? TEAM_LIST_VIEW_CONFIG : LIST_VIEW_CONFIG;
  }
  return DEFAULT_VIEW_CONFIG;
}

// Every member is optional and every member catches: a config written by a
// build that did not have a key yet, or one hand-edited into nonsense, must
// still open the view. A key that fails validation falls back to its default
// rather than failing the parse — a broken "Only me" flag is not a reason to
// refuse to render.
const viewConfigSchema = z.object({
  onlyMe: z.boolean().catch(DEFAULT_VIEW_CONFIG.onlyMe),
  showArchived: z.boolean().catch(DEFAULT_VIEW_CONFIG.showArchived),
  showShared: z.boolean().catch(DEFAULT_VIEW_CONFIG.showShared),
  showCompleted: z.boolean().catch(DEFAULT_VIEW_CONFIG.showCompleted),
  showCanceled: z.boolean().catch(DEFAULT_VIEW_CONFIG.showCanceled),
  collapseEmptyStages: z
    .boolean()
    .catch(DEFAULT_VIEW_CONFIG.collapseEmptyStages),
  groupBy: z.string().catch(DEFAULT_VIEW_CONFIG.groupBy),
  groupDirection: z.enum(["asc", "desc"]).catch(DEFAULT_VIEW_CONFIG.groupDirection),
  collapsedGroups: z
    .array(z.string())
    .catch(DEFAULT_VIEW_CONFIG.collapsedGroups),
  expandedGroups: z
    .array(z.string())
    .catch(DEFAULT_VIEW_CONFIG.expandedGroups),
  sortBy: z.string().catch(DEFAULT_VIEW_CONFIG.sortBy),
  sortDirection: z.enum(["asc", "desc"]).catch(DEFAULT_VIEW_CONFIG.sortDirection),
  columns: viewColumnsSchema.catch(DEFAULT_VIEW_CONFIG.columns),
  card: cardDisplayOptionsSchema
    .partial()
    // `.partial()` lands every key on the object, `undefined` for the ones the
    // stored card omitted — spreading those raw would erase the defaults.
    .transform((card): CardDisplayOptions => ({
      ...DEFAULT_CARD_DISPLAY_OPTIONS,
      ...(Object.fromEntries(
        Object.entries(card).filter(([, value]) => value !== undefined),
      ) as Partial<CardDisplayOptions>),
      // The "Stack fields" switch is hidden from the panel (the wrap layout's
      // UX is unsettled), so a stored `wrap` reads as stacked: with no control
      // to change it back, honoring it would pin the view to a layout nobody
      // can leave. Saving the view writes `list` over it.
      fieldLayout: "list",
    }))
    .catch(DEFAULT_VIEW_CONFIG.card),
  cardSize: z.enum(["medium", "large"]).catch(DEFAULT_VIEW_CONFIG.cardSize),
  filters: filterStateSchema.catch(DEFAULT_VIEW_CONFIG.filters),
  advancedFilters: filterStateSchema.catch(DEFAULT_VIEW_CONFIG.advancedFilters),
  autoRefresh: z.boolean().catch(DEFAULT_VIEW_CONFIG.autoRefresh),
  autoLayout: z.boolean().catch(DEFAULT_VIEW_CONFIG.autoLayout),
});

/**
 * Read a persisted `EntityView.config` into the shape the panel and the views
 * consume. Anything unparseable becomes `defaults` — the body's
 * (`viewConfigDefaults`) when the caller knows the view's type — so this never
 * throws and never returns a partial object.
 */
export function parseViewConfig(
  raw: unknown,
  defaults: ViewConfig = DEFAULT_VIEW_CONFIG,
): ViewConfig {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return defaults;
  }
  const source = raw as Record<string, unknown>;
  return {
    onlyMe: viewConfigSchema.shape.onlyMe.parse(source.onlyMe),
    showArchived: viewConfigSchema.shape.showArchived.parse(source.showArchived),
    showShared: viewConfigSchema.shape.showShared.parse(source.showShared),
    showCompleted: viewConfigSchema.shape.showCompleted.parse(
      source.showCompleted,
    ),
    showCanceled: viewConfigSchema.shape.showCanceled.parse(source.showCanceled),
    // The two keys whose fallback is the body's, not the shared default: a
    // list stored before the key existed (or seeded with `{}`) opens the way
    // a new one does — a team's list ungrouped, a workflow's by stage.
    collapseEmptyStages: z
      .boolean()
      .catch(defaults.collapseEmptyStages)
      .parse(source.collapseEmptyStages),
    groupBy: z.string().catch(defaults.groupBy).parse(source.groupBy),
    groupDirection: viewConfigSchema.shape.groupDirection.parse(
      source.groupDirection,
    ),
    collapsedGroups: viewConfigSchema.shape.collapsedGroups.parse(
      source.collapsedGroups,
    ),
    expandedGroups: viewConfigSchema.shape.expandedGroups.parse(
      source.expandedGroups,
    ),
    sortBy: viewConfigSchema.shape.sortBy.parse(source.sortBy),
    sortDirection: viewConfigSchema.shape.sortDirection.parse(
      source.sortDirection,
    ),
    // Absent (a row seeded with `{}`), the body's own default order stands —
    // a team's list leads with Follow up, a workflow's with the table's.
    columns:
      source.columns === undefined
        ? defaults.columns
        : viewConfigSchema.shape.columns.parse(source.columns),
    card: viewConfigSchema.shape.card.parse(source.card),
    cardSize: viewConfigSchema.shape.cardSize.parse(source.cardSize),
    filters: viewConfigSchema.shape.filters.parse(source.filters),
    advancedFilters: viewConfigSchema.shape.advancedFilters.parse(
      source.advancedFilters,
    ),
    autoRefresh: viewConfigSchema.shape.autoRefresh.parse(source.autoRefresh),
    autoLayout: viewConfigSchema.shape.autoLayout.parse(source.autoLayout),
  };
}

/**
 * The stage predicate a view's visibility toggles add to its base where, or
 * null when both are on. The issue filter offers `stageId: { in }` and no
 * exclusion, so hiding the done or canceled stages means naming every stage
 * the view KEEPS — from the whole workspace's stages, so a cross-workflow
 * scope keeps the other workflows' open stages too (the scope predicate
 * narrows the workflow). A stage with no type is kept: the toggles hide only
 * what is known to be closed.
 */
export function viewStageWhere<S extends { stageId: string; type?: string | null }>(
  config: Pick<ViewConfig, "showCompleted" | "showCanceled">,
  stages: readonly S[],
): Record<string, unknown> | null {
  if (config.showCompleted && config.showCanceled) return null;
  const hidden = new Set<string>();
  if (!config.showCompleted) hidden.add("done");
  if (!config.showCanceled) hidden.add("canceled");
  const kept = stages
    .filter((stage) => !stage.type || !hidden.has(stage.type))
    .map((stage) => stage.stageId);
  return { stageId: { in: kept } };
}

/** Whether a filter `where` names the archived field anywhere in it. */
function namesArchived(where: unknown): boolean {
  if (Array.isArray(where)) return where.some(namesArchived);
  if (where === null || typeof where !== "object") return false;
  return Object.entries(where).some(
    ([key, value]) => key === "archived" || namesArchived(value),
  );
}

/**
 * The archived predicate a view adds to its base where, or null when it adds
 * none. Off, "Show archived" hides the archived projects. A person's own
 * filter on the archived field ("Archived", or an advanced rule) decides for
 * itself: ANDed with the view's exclusion, "Archived" would match nothing.
 */
export function viewArchivedWhere(
  config: Pick<ViewConfig, "showArchived">,
  ...filterWheres: ReadonlyArray<Record<string, unknown> | null | undefined>
): Record<string, unknown> | null {
  if (config.showArchived) return null;
  if (filterWheres.some(namesArchived)) return null;
  return { archived: { eq: false } };
}

/** The config keys the panel counts on its Save button. */
const VIEW_CONFIG_KEYS = Object.keys(DEFAULT_VIEW_CONFIG) as Array<
  keyof ViewConfig
>;

/**
 * Which top-level keys differ between the saved config and the draft. Deep
 * members compare structurally — a card toggle is one
 * unsaved change, not thirteen — so the Save button counts what a person
 * would call a change.
 */
export function viewConfigDirtyKeys(
  saved: ViewConfig,
  draft: ViewConfig,
): Array<keyof ViewConfig> {
  return VIEW_CONFIG_KEYS.filter(
    (key) => JSON.stringify(saved[key]) !== JSON.stringify(draft[key]),
  );
}
